import React, { memo } from "react";

import { formatNumber, useTranslation } from "utils";

import { FieldListField, PRACTICE_SLUG, PracticeFCPO } from "models";

import { Flex, Icon, Link, Typography } from "components";

import { BulkEditCrop } from "pages/app/all-field-view/components/BulkEditCropDrawer.tsx";
import { FieldMap } from "pages/app/historical-practice-data-table-view/components/FieldMap";

export type FieldHeadlineBulkProps = {
  affectedFields: FieldListField[];
  selectedCrops: BulkEditCrop[];
  selectedTillages: PracticeFCPO[];
  selectedFertilizers: PracticeFCPO[];
  showDetails: boolean;
  onToggleDetails: () => void;
};

const BREAKDOWN_PILL_KEYS = ["crop", "growingPeriod", "yield", "seeding", "residueManagement", "tillage", "organicFertilizer"] as const;

export const FieldHeadlineBulk = memo(
  ({ affectedFields, selectedCrops, selectedTillages, selectedFertilizers, showDetails, onToggleDetails }: FieldHeadlineBulkProps) => {
    const { t } = useTranslation(["allFieldView", "fieldTimeline"]);

    const totalHa = affectedFields.reduce((acc, field) => acc + field.size, 0);

    // reflects each field's saved data, not the bulk form's own unsaved edits
    const getFieldBreakdown = (field: FieldListField): Record<(typeof BREAKDOWN_PILL_KEYS)[number], boolean> => {
      const crop = selectedCrops.find((selectedCrop) => selectedCrop.fieldId === field.id);
      return {
        crop: !!crop?.cropId,
        growingPeriod: !!crop?.seedingStartDate && !!crop?.harvestEndDate,
        yield: crop?.yieldPerHa != null,
        seeding: !!crop?.seedingPracticeType,
        residueManagement: crop?.hasResidue != null,
        tillage: !!crop && selectedTillages.some((practice) => practice.cropId === crop.id),
        organicFertilizer: !!crop && selectedFertilizers.some((practice) => practice.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS && practice.cropId === crop.id),
      };
    };

    return (
      <>
        <Flex align="center" justify="space-between" className="field-headline-bulk-summary">
          <Flex align="center" gap="0.75rem">
            <Flex align="center" justify="center" className="field-headline-bulk-summary-icon">
              <Icon iconName="tabler/copy" size="m-light" />
            </Flex>
            <Flex vertical>
              <Typography variant="paragraph-m" bold className="margin-0">
                {t("allFieldView:cropsSelected", { count: selectedCrops.length })}
              </Typography>
              <Typography variant="paragraph-s" color="grey-600" className="margin-0">
                {`${formatNumber({ number: totalHa })} ${t("allFieldView:fieldHeadlineCard.ha")}`}
              </Typography>
            </Flex>
          </Flex>
          <Link dataTestId="field-headline-bulk-toggle-details" type="primary-link" onClick={onToggleDetails}>
            <Flex align="center" gap="0.25rem">
              {t(showDetails ? "fieldTimeline:addCrops.hideDetails" : "fieldTimeline:addCrops.showDetails")}
              <Icon iconName={showDetails ? "tabler/chevron-up" : "tabler/chevron-down"} size="xs" />
            </Flex>
          </Link>
        </Flex>
        {showDetails && (
          <Flex vertical gap="0.5rem" className="field-headline-bulk-field-list">
            {affectedFields.map((field) => {
              const breakdown = getFieldBreakdown(field);
              return (
                <div key={field.id} className="field-headline-bulk-field-list-item">
                  <Flex align="flex-start" gap="1.25rem">
                    <div className="field-headline-bulk-field-list-thumbnail">
                      <FieldMap field={field} />
                    </div>
                    <Flex vertical gap="0.5rem" className="field-headline-bulk-field-list-details">
                      <Flex align="center" gap="0.5rem">
                        <Typography variant="paragraph-m" bold className="margin-0">
                          {field.name}
                        </Typography>
                        <Typography variant="paragraph-s" color="grey-600" className="margin-0">
                          {`${formatNumber({ number: field.size })} ${t("allFieldView:fieldHeadlineCard.ha")}`}
                        </Typography>
                      </Flex>
                      <Flex wrap="wrap" gap="0.5rem" className="field-headline-bulk-field-list-pills">
                        {BREAKDOWN_PILL_KEYS.map((pillKey) => (
                          <Flex key={pillKey} align="center" gap="0.25rem" className={`field-headline-bulk-pill ${breakdown[pillKey] ? "filled" : ""}`}>
                            <span className="field-headline-bulk-pill-dot" />
                            <Typography variant="paragraph-s" className="margin-0">
                              {t(`allFieldView:fieldHeadlineBulkPill.${pillKey}`)}
                            </Typography>
                          </Flex>
                        ))}
                      </Flex>
                    </Flex>
                  </Flex>
                </div>
              );
            })}
          </Flex>
        )}
      </>
    );
  }
);
